import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Globe2, Landmark, ShieldCheck, Building2, HandCoins, Users2, Loader2, Search } from "lucide-react";

const TYPE_META = {
  ministry: { label: "Ministries", icon: Landmark, color: "#D4AF37" },
  regulator: { label: "Regulators", icon: ShieldCheck, color: "#3B82F6" },
  agency: { label: "Agencies & SOEs", icon: Building2, color: "#22C55E" },
  dfi: { label: "DFIs", icon: HandCoins, color: "#F59E0B" },
  donor: { label: "Donors", icon: Users2, color: "#EC4899" },
  regional: { label: "Regional Bodies", icon: Globe2, color: "#A855F7" },
};

const fmtBn = (n) => (n == null ? "—" : n >= 100 ? `$${n.toFixed(0)}B` : `$${n.toFixed(1)}B`);
const fmtPct = (n) => (n == null ? "—" : `${n.toFixed(1)}%`);

export default function Africa() {
  const [countries, setCountries] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/africa/countries").then((r) => {
      setCountries(r.data);
      const gh = r.data.find((c) => c.code === "GH") || r.data[0];
      if (gh) setActive(gh.code);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!active) return;
    setDetail(null);
    api.get(`/africa/country/${active}`).then((r) => setDetail(r.data));
  }, [active]);

  const filteredCrossCutting = useMemo(() => {
    if (!detail) return {};
    const term = search.toLowerCase().trim();
    if (!term) return detail.cross_cutting_by_type;
    const out = {};
    for (const [t, list] of Object.entries(detail.cross_cutting_by_type)) {
      const f = list.filter(
        (i) => i.name.toLowerCase().includes(term) || (i.short || "").toLowerCase().includes(term) || (i.mandate || "").toLowerCase().includes(term)
      );
      if (f.length) out[t] = f;
    }
    return out;
  }, [detail, search]);

  if (loading) {
    return (
      <div className="p-10 text-slate-400 font-mono flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading sovereign intelligence layer…
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="africa-page">
      {/* Header */}
      <div>
        <div className="label-eyebrow mb-1">// Sovereign & Country Intelligence</div>
        <h1 className="font-display text-3xl text-white tracking-tight">Africa Intelligence Layer</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          Live snapshot of African macro indicators, government priorities, regulators, DFIs, and donor landscape — the
          institutional reference layer every Pandora engagement runs on top of.
        </p>
      </div>

      {/* Country selector */}
      <div className="flex flex-wrap gap-2" data-testid="country-list">
        {countries.map((c) => (
          <button
            key={c.code}
            onClick={() => setActive(c.code)}
            data-testid={`country-${c.code}`}
            className={`px-3.5 py-2 border transition-all ${
              active === c.code
                ? "border-[#D4AF37]/60 bg-[#D4AF37]/8"
                : "border-white/10 hover:border-white/25"
            }`}
          >
            <div className="font-mono text-[10px] text-slate-500 tracking-widest">{c.code}</div>
            <div className="text-sm text-white">{c.name}</div>
          </button>
        ))}
      </div>

      {!detail ? (
        <div className="p-10 text-slate-400 font-mono flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading country profile…
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="country-detail">
          {/* Country Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 nexus-glass-panel p-6">
              <div className="label-eyebrow">// {detail.country.region}</div>
              <h2 className="font-display text-3xl text-white mt-1">{detail.country.name}</h2>
              <p className="text-slate-400 text-sm mt-1">
                Capital · {detail.country.capital} · Currency · {detail.country.currency} · Language · {detail.country.language}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                <Indicator label="GDP" value={fmtBn(detail.country.indicators.gdp_usd_bn)} accent />
                <Indicator label="Growth" value={fmtPct(detail.country.indicators.gdp_growth_pct)} />
                <Indicator label="Inflation" value={fmtPct(detail.country.indicators.inflation_pct)} />
                <Indicator label="Policy Rate" value={fmtPct(detail.country.indicators.policy_rate_pct)} />
                <Indicator label="Population" value={detail.country.indicators.population_m ? `${detail.country.indicators.population_m}M` : "—"} />
                <Indicator label="Fiscal Deficit" value={fmtPct(detail.country.indicators.fiscal_deficit_pct_gdp)} />
                <Indicator label="EoB Rank" value={detail.country.indicators.ease_of_business_rank || "—"} />
                <Indicator label="Rating" value={detail.country.indicators.sovereign_rating || "—"} />
              </div>
            </div>

            <div className="nexus-glass-panel p-6 space-y-4">
              <div>
                <div className="label-eyebrow mb-2">// Government Priorities</div>
                <ul className="space-y-1.5">
                  {detail.country.government_priorities.map((p, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-200 leading-snug">
                      <span className="font-mono text-[10px] text-[#D4AF37] mt-1">{String(i + 1).padStart(2, "0")}</span>
                      <span className="flex-1">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Investment & Policy Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="nexus-glass-panel p-5">
              <div className="label-eyebrow mb-2">// Investment Climate</div>
              <p className="text-sm text-slate-200 leading-relaxed">{detail.country.investment_climate}</p>
            </div>
            <div className="nexus-glass-panel p-5">
              <div className="label-eyebrow mb-2">// Policy Environment</div>
              <p className="text-sm text-slate-200 leading-relaxed">{detail.country.policy_environment}</p>
            </div>
          </div>

          {/* Country-Resident Institutions */}
          <div>
            <h3 className="font-display text-lg text-white mb-3">Resident Institutions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {["ministry", "regulator", "agency"].map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                const list = detail.institutions_by_type[t] || [];
                return (
                  <div key={t} className="nexus-glass-panel p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} strokeWidth={1.6} />
                      <span className="label-eyebrow">{meta.label}</span>
                      <span className="ml-auto font-mono text-[10px] text-slate-500">{list.length}</span>
                    </div>
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {list.length === 0 && <div className="text-[12px] text-slate-500">— no data —</div>}
                      {list.map((inst) => (
                        <InstitutionCard key={inst.id} inst={inst} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cross-cutting */}
          <div>
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <h3 className="font-display text-lg text-white">Cross-Cutting Partners (DFIs · Donors · Regional)</h3>
              <div className="relative">
                <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="africa-search"
                  placeholder="Search DFIs / donors / regional bodies"
                  className="bg-[#0A111F] border border-white/10 text-white text-xs px-7 py-1.5 w-72 focus:border-[#D4AF37]/60 focus:outline-none"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {["dfi", "donor", "regional"].map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                const list = filteredCrossCutting[t] || [];
                return (
                  <div key={t} className="nexus-glass-panel p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} strokeWidth={1.6} />
                      <span className="label-eyebrow">{meta.label}</span>
                      <span className="ml-auto font-mono text-[10px] text-slate-500">{list.length}</span>
                    </div>
                    <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                      {list.map((inst) => (
                        <InstitutionCard key={inst.id} inst={inst} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

const Indicator = ({ label, value, accent }) => (
  <div className="p-3 border border-white/5 bg-white/[0.02]">
    <div className="label-eyebrow">{label}</div>
    <div className="font-display text-lg mt-1" style={{ color: accent ? "var(--gold-400)" : "#F8FAFC" }}>
      {value}
    </div>
  </div>
);

const InstitutionCard = ({ inst }) => (
  <div className="p-2.5 border border-white/5 hover:border-white/15 hover:bg-white/[0.03] transition-all">
    <div className="flex items-baseline gap-2">
      <span className="text-sm text-white truncate flex-1">{inst.name}</span>
      {inst.short && (
        <span className="font-mono text-[10px] text-[#D4AF37] tracking-widest">{inst.short}</span>
      )}
    </div>
    {inst.mandate && (
      <div className="text-[11px] text-slate-400 mt-1 leading-snug line-clamp-2">{inst.mandate}</div>
    )}
  </div>
);
